import { Grid, Paper, Tab, Tabs, Typography } from '@mui/material'
import PrimeTable from '../../../../components/PrimeTable'
import { metodoPrograma } from '../../../../components/api'
import { isEqual } from 'lodash'
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import dayjs from 'dayjs'
import { GlobalContext } from '../../../../App'

const apenasValor = (valor) => Number(String(valor).replace(/[^\d.,]/g, ''))

const formatCurrency = (v) =>
  (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const CardValor = ({ label, value, color }) => (
  <Paper style={{ backgroundColor: color, padding: '12px 16px' }}>
    <Typography style={{ fontSize: 11, color: 'white' }}>{label}</Typography>
    <Typography
      style={{
        fontWeight: 600,
        fontSize: '1.2em',
        color: 'white',
        marginTop: 2,
      }}
    >
      {formatCurrency(apenasValor(value))}
    </Typography>
  </Paper>
)

const LinhaResumo = ({ label, value, bold }) => (
  <Grid
    container
    justifyContent="space-between"
    style={{
      padding: '6px 0',
      borderBottom: '1px solid #e0e0e0',
    }}
  >
    <Typography style={{ fontSize: 13, fontWeight: bold ? 600 : 400 }}>
      {label}
    </Typography>
    <Typography style={{ fontSize: 13, fontWeight: bold ? 600 : 400 }}>
      {formatCurrency(value)}
    </Typography>
  </Grid>
)

const Visualiza = ({
  dados,
  setDadosEdicao,
  setTela,
  refreshKey,
  idRegistro,
}) => {
  const { localAtendimento } = useContext(GlobalContext)
  const [lista, setLista] = useState([])
  const [entradas, setEntradas] = useState(0.0)
  const [saidas, setSaidas] = useState(0.0)
  const [resultado, setResultado] = useState(0.0)
  const [tabIndex, setTabIndex] = useState(0)

  const vlAbertura = dados?.[10] ?? 0

  const getList = useCallback(() => {
    metodoPrograma('listaLancamentosCaixa', {
      idCaixa: dados[0],
      idRegistro,
      idLocalAtendimento: localAtendimento[0],
    }).then((res) => {
      if (res.status) {
        setEntradas(res.valorEntrada ?? 0)
        setSaidas(res.valorSaida ?? 0)
        setResultado(res.totalGeral ?? 0)
        setLista(res.lista || [])
      }
    })
  }, [dados, localAtendimento, idRegistro])

  useEffect(() => {
    if (dados[0]) {
      getList()
    }
  }, [dados, getList, refreshKey])

  const totaisPorFormaPagamento = useMemo(() => {
    const mapa = {}
    lista.forEach((item) => {
      const fp = item[3][1]
      if (!mapa[fp]) mapa[fp] = { entradas: 0, saidas: 0 }
      if (item[4][0] === 'E') {
        mapa[fp].entradas += item[5]
      } else {
        mapa[fp].saidas += item[5]
      }
    })
    return Object.entries(mapa).map(([forma, vals]) => ({
      forma,
      ...vals,
      total: vals.entradas - vals.saidas,
    }))
  }, [lista])

  const handleClickInLine = (item) => {
    setDadosEdicao(item)
    setTela('edicao')
  }

  const handleRemove = useCallback(
    (linha) => {
      metodoPrograma('excluiLancamentoCaixa', {
        idLancamento: linha[6],
        idRegistro,
        idCaixa: dados[0],
      }).then((res) => {
        if (res.status) {
          getList()
        }
      })
    },
    [getList, dados, idRegistro],
  )

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Resumo" value={0} />
          <Tab label="Detalhamento" value={1} />
        </Tabs>
      </Grid>

      {tabIndex === 0 && (
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper style={{ padding: 16, width: '100%', height: '100%' }}>
                <Typography
                  variant="subtitle1"
                  style={{ fontWeight: 600, marginBottom: 12 }}
                >
                  Conferência de Caixa
                </Typography>
                <PrimeTable
                  dadosTable={lista}
                  disablePagination
                  head={['Data', 'Descrição', 'Tipo', 'Valor']}
                  line={(item) => [item[0], item[1], item[4][1], item[5]]}
                  collumnsDate={[0]}
                  collumnsCurrency={[3]}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} container>
              <Paper style={{ padding: 16, width: '100%' }}>
                <Typography
                  variant="subtitle1"
                  style={{ fontWeight: 600, marginBottom: 12 }}
                >
                  Resumo Financeiro
                </Typography>
                <LinhaResumo label="Total Entradas" value={entradas || 0} />
                <LinhaResumo label="Total Saídas" value={saidas || 0} />
                <LinhaResumo label="Saldo" value={resultado || 0} bold />
                <Typography
                  variant="subtitle2"
                  style={{ fontWeight: 600, marginTop: 20, marginBottom: 8 }}
                >
                  Por Forma de Pagamento
                </Typography>
                {totaisPorFormaPagamento.map((item) => (
                  <Paper
                    key={item.forma}
                    variant="outlined"
                    style={{ padding: '8px 12px', marginBottom: 6 }}
                  >
                    <Grid
                      container
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography style={{ fontSize: 13, fontWeight: 500 }}>
                        {item.forma}
                      </Typography>
                      <Typography style={{ fontSize: 12 }}>
                        Entradas: {formatCurrency(apenasValor(item.entradas))} |
                        Saídas: {formatCurrency(apenasValor(item.saidas))}
                      </Typography>
                    </Grid>
                  </Paper>
                ))}
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      )}

      {tabIndex === 1 && (
        <Grid item xs={12}>
          <Paper style={{ padding: 16, marginBottom: 16 }}>
            <Typography variant="body2" color="textSecondary">
              Aberto por: <strong>{dados?.[8]?.[1] || '-'}</strong> em{' '}
              <strong>
                {dados?.[2] ? dayjs(dados[2]).format('DD/MM/YYYY HH:mm') : '-'}
              </strong>
            </Typography>
          </Paper>

          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            <Grid item xs={6} sm={3}>
              <CardValor
                label="Abertura"
                value={vlAbertura}
                color="rgb(158 158 158 / 61%)"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <CardValor
                label="Entradas"
                value={entradas}
                color="rgb(67 160 71 / 61%)"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <CardValor
                label="Saídas"
                value={saidas}
                color="rgb(211 47 47 / 61%)"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <CardValor
                label="Saldo"
                value={apenasValor(vlAbertura) + apenasValor(resultado)}
                color="rgb(25 118 210 / 61%)"
              />
            </Grid>
          </Grid>
          <PrimeTable
            dadosTable={lista}
            disablePagination
            head={[
              'Data',
              'Descrição',
              'Usuário',
              'Forma Pagamento',
              'Tipo',
              'Valor',
              '',
            ]}
            line={(item) => [
              item[0],
              item[1],
              item[2][1],
              item[3][1],
              item[4][1],
              item[5],
              item[6],
            ]}
            collumnsDate={[0]}
            collumnsCurrency={[5]}
            onDelete={{ index: 10, action: handleRemove }}
            onRowSelect={handleClickInLine}
          />
        </Grid>
      )}
    </Grid>
  )
}

export default memo(Visualiza, isEqual)
